import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { logDealView, resolveDealId } from '@/lib/api/myTenders';
import { format } from 'date-fns';
import { ArrowRight, ChevronLeft, ExternalLink, FileText } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { normalizeDeal, getDecisionWindowBandForClosedDeal, resolveAttachmentUrl } from '@/utils/procurementDealUtils';
import { bundledContractNote } from '@/utils/procurementCategoryUtils';
import { getDealValueSource } from '@/utils/procurementValueUsd';
import { procurementMethodDisplay } from '@/lib/procurementMethod';
import { AccountFinal } from '@/api/entities';
import { extractAccountPageId } from '@/lib/marketExperience';
import { fetchMarketAccountTechStack } from '@/lib/marketExperienceApi';
import { categoryHeading, partitionStackByRole, prepareCategoryVendors, vendorProductLabel } from '@/lib/marketTechStack';
import { createPageUrl } from '@/utils';
import { toCategoryKeyForApi } from '@/utils/edtechCategoryNames';
import {
  decisionWindowStart,
  expiryGrade,
  expiryGradeLabel,
  clipTitle,
  formatNativeValue,
  formatUsdShort,
  isDisplayableStackLabel,
  marketShareRows,
  monthsAwayText,
  pricePeerRows,
  registerLabel,
  sentimentEvidenceMatchesVendor,
  sentimentLevel,
  specificNoticeLink,
  timelineProgress,
} from '@/lib/closedDealPresentation';
import DealValueDisplay from './DealValueDisplay';
import { isHardwareTenant, isHardwareCategory, purchaseKind, bandForAge, ageInYears, bandsFor } from '@/lib/replacementCycle';

// [2026-11-01] Redesign (Tiago): the old page was a 22-row table that printed every field, filled
// or not, plus the pipeline's own notes. It is now ordered around the decision — when it renews,
// how safe the incumbent is, what else the institution runs, what it cost against peers — and a
// block with no data is removed rather than shown as dashes. Rules: src/lib/closedDealPresentation.js.

const safeDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};
const monthYear = (v) => {
  const d = safeDate(v);
  return d ? format(d, 'MMM yyyy') : null;
};
const fullDate = (v) => {
  const d = safeDate(v);
  return d ? format(d, 'd MMM yyyy') : null;
};

// [2026-10-21] Honour VITE_API_BASE_URL on localhost, else the deployed API (see git history).
const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? (import.meta.env.VITE_API_BASE_URL || 'https://api.neogtm.com')
  : 'https://api.neogtm.com';

const STATUS_LABEL = { active: 'Active deal', live: 'Active deal', expired: 'Expired', retired: 'Ended' };

function Card({ children, className = '' }) {
  return <section className={`bg-white rounded-2xl p-6 md:p-7 shadow-sm ${className}`}>{children}</section>;
}

function Eyebrow({ children }) {
  return <div className="text-xs font-semibold uppercase tracking-wider text-[#0A7F86]">{children}</div>;
}

function FactTile({ label, children, note, accent = false }) {
  return (
    <div className={`${accent ? 'bg-[#0A5A6B] text-white' : 'bg-white'} p-5`}>
      <div className={`text-xs uppercase tracking-wider ${accent ? 'text-[#BFE9F0]' : 'text-[#5C6B7A]'}`}>{label}</div>
      <div className="mt-1.5 text-2xl font-semibold leading-tight">{children}</div>
      {note ? <div className={`mt-1 text-[13px] ${accent ? 'text-[#BFE9F0]' : 'text-[#5C6B7A]'}`}>{note}</div> : null}
    </div>
  );
}

// One plain explanation for every badge (Tiago: "generic and client friendly").
const GRADE_HELP = 'How sure we are about this date. High: taken from the contract itself. Medium: worked out from the contract length. Low: our best estimate.';

function GradeBadge({ grade }) {
  if (!grade) return null;
  const tone = grade === 'H' || grade === 'T' ? 'bg-[#E6F4F6] text-[#0A5A6B]' : grade === 'M' ? 'bg-[#FFF4D6] text-[#7A4B00]' : 'bg-gray-100 text-gray-700';
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" className={`inline-block cursor-help rounded-full px-2 py-0.5 text-xs font-semibold ${tone}`} aria-label={GRADE_HELP}>
          {expiryGradeLabel(grade)}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[260px] text-xs leading-relaxed">{GRADE_HELP}</TooltipContent>
    </Tooltip>
  );
}

function useTenantAccountPage(tenantId, poolAccountId) {
  const [pageId, setPageId] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setPageId(null);
    if (!tenantId || !poolAccountId) return undefined;
    (async () => {
      try {
        const rows = await AccountFinal.filter({ tenant_id: tenantId, global_pool_account_id: poolAccountId }, '', 5);
        // An account removed from the list keeps its row (is_visible=false) — no page to open.
        const visible = (rows || []).find((r) => r && r.is_visible !== false);
        const id = visible ? extractAccountPageId(visible) : null;
        if (!cancelled) setPageId(id);
      } catch {
        /* no button rather than a link that 404s */
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId, poolAccountId]);
  return pageId;
}

// [2026-11-02] Tiago: not every vendor-state record across all categories — show the deal's own
// category first, then the LMS and SIS that every integration runs through. At most three tiles.
function useTechStack(tenantId, poolAccountId, dealCategory) {
  const [stack, setStack] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setStack(null);
    if (!tenantId || !poolAccountId) return undefined;
    (async () => {
      try {
        const data = await fetchMarketAccountTechStack(tenantId, poolAccountId, undefined, dealCategory || undefined);
        if (cancelled || data?.resolved === false) return;
        const { lens, context } = partitionStackByRole(Array.isArray(data?.stack) ? data.stack : []);
        const byKey = new Map([...lens, ...context].map((cat) => [toCategoryKeyForApi(cat?.category), cat]));
        const wanted = [...new Set([toCategoryKeyForApi(dealCategory), 'lms', 'sis'].filter(Boolean))];
        const tiles = wanted
          .map((key) => byKey.get(key))
          .filter(Boolean)
          .map((cat) => {
            const vendors = prepareCategoryVendors(cat?.vendors).map(vendorProductLabel).filter(isDisplayableStackLabel);
            const heading = categoryHeading(cat, tenantId);
            return heading && vendors.length ? { heading, vendors } : null;
          })
          .filter(Boolean);
        setStack(tiles);
      } catch {
        /* market module off for this tenant: the block is removed */
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId, poolAccountId, dealCategory]);
  return stack;
}

export default function ClosedDealDetailPage({ deal, onBack, tenantId, logViews = false, peerDeals = [] }) {
  // [2026-10-10] The list payload omits the detail-only columns; fetch the one row being viewed.
  const [full, setFull] = useState(null);
  // Only merge a fetched row that belongs to THIS deal. Opening another deal from the price
  // comparison changed `deal` while `full` still held the previous row, whose deal_id then won the
  // merge — the page kept showing the old deal and never fetched the new one.
  const fullMatches = full && deal && String(full.deal_id ?? '') === String(resolveDealId(deal) ?? '');
  const baseDeal = deal ? (fullMatches ? { ...deal, ...full } : deal) : null;
  const d = baseDeal ? normalizeDeal(baseDeal, baseDeal.account) : null;
  const dealId = d ? resolveDealId(d) : null;

  useEffect(() => {
    let cancelled = false;
    setFull(null);
    if (!dealId || !tenantId) return undefined;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/procurement/query?table=closed&dealId=${encodeURIComponent(dealId)}`,
          { headers: { 'x-tenant-id': tenantId }, credentials: 'include' },
        );
        if (!res.ok) return;
        const body = await res.json();
        const row = body?.data?.[0];
        if (row && !cancelled) setFull(row);
      } catch {
        /* non-fatal: the page renders from the list fields */
      }
    })();
    return () => { cancelled = true; };
  }, [dealId, tenantId]);

  // Opened from a scrolled list, the page would otherwise start half way down.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [dealId]);

  useEffect(() => {
    if (!logViews || !tenantId || !dealId) return;
    logDealView(tenantId, dealId);
  }, [logViews, tenantId, dealId]);

  const raw = baseDeal || {};
  const poolAccountId = raw.account_id ? String(raw.account_id).trim() : null;
  const accountPageId = useTenantAccountPage(tenantId, poolAccountId);
  const stack = useTechStack(tenantId, poolAccountId, raw.category);

  const market = useMemo(() => (raw.category ? marketShareRows(raw, peerDeals) : null), [raw.category, raw.country, peerDeals]); // eslint-disable-line react-hooks/exhaustive-deps
  const pricePeers = useMemo(() => (raw.category ? pricePeerRows(raw, peerDeals) : null), [raw.category, raw.country, raw.deal_id, peerDeals]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!deal || !d) return null;

  const institution = d.institution || raw.account_name;
  const country = d.country || raw.country;
  const product = d.product_name || raw.product_name;
  const supplier = d.supplier || raw.supplier;
  const category = raw.category || d.category;
  const statusLabel = STATUS_LABEL[String(raw.status ?? '').trim().toLowerCase()] || null;
  const enrolment = Number(raw.enrolment) > 0 ? Number(raw.enrolment).toLocaleString() : null;
  const institutionLine = [institution, country, raw.institution_type, enrolment ? `${enrolment} students` : null].filter(Boolean).join(' · ');

  const valueSource = getDealValueSource(raw);
  const hasValue = valueSource !== 'none';
  const bundleNote = bundledContractNote(raw);
  const { method, framework } = procurementMethodDisplay(raw);
  const awarded = monthYear(raw.first_award_date ?? raw.published_date);
  const term = Number(raw.initial_term ?? raw.initial_term_months);
  const extension = Number(raw.extension ?? raw.extension_months);
  const grade = expiryGrade(raw);
  const expiry = raw.predicted_expiry ?? raw.predicted_expiry_date;
  const { band, months } = getDecisionWindowBandForClosedDeal(raw);
  const expired = band === 'Expired';
  const showExpiry = Boolean(safeDate(expiry)) && Boolean(grade);
  const lowGrade = grade === 'L';
  const progress = showExpiry ? timelineProgress(raw) : null;
  const windowStart = showExpiry ? decisionWindowStart(raw) : null;
  const windowOpen = showExpiry && !expired && months != null && months <= 12;
  const bidders = raw.number_of_bidders;

  // [2026-11-03] Hardware tenants (bibliotheca, niryo): a SYSTEM purchase of capital kit is read by
  // equipment age, not by contract expiry — the kit stays installed long after the contract ends.
  // Service contracts keep the renewal timeline below; consumables never date the install.
  // 'other' = an IT Hardware buy that is not part of the device fleet (servers, printers…): normal view.
  const hwKindRaw = isHardwareTenant(tenantId) && isHardwareCategory(category) ? purchaseKind({ ...raw, category }) : null;
  const hwKind = hwKindRaw === 'other' ? null : hwKindRaw;
  const BAND_YEARS = bandsFor(category);
  const hwSystem = hwKind === 'system';
  const hwAge = hwSystem ? ageInYears(raw.first_award_date ?? raw.published_date) : null;
  const hwBand = hwSystem ? bandForAge(hwAge, category) : null;
  const HW_BAND_TEXT = {
    new: 'New', mid: 'Mid-life', plan: 'Start planning', due: 'Due for replacement', overdue: 'Overdue', undated: 'Purchase date not published',
  };

  // An ended contract does not end the kit, so hardware keeps its sentiment.
  const sentiment = expired && !hwKind ? null : sentimentLevel(raw.incumbent_sentiment);
  const sentimentOk = Boolean(sentiment) && sentimentEvidenceMatchesVendor(raw);
  const rationale = sentimentOk ? String(raw.incumbent_sentiment_rationale ?? '').trim() : '';

  const register = registerLabel(raw);
  const noticeUrl = specificNoticeLink(raw);
  const originalTitle = clipTitle(raw.contract_scope);
  const attachments = [];
  const toArr = (v) => {
    if (Array.isArray(v)) return v;
    if (typeof v === 'string') {
      try { const p = JSON.parse(v); return Array.isArray(p) ? p : [v]; } catch { return v ? [v] : []; }
    }
    return [];
  };
  toArr(raw.pdf_attachment_urls).concat(toArr(raw.attachment_urls)).forEach((u) => {
    const href = resolveAttachmentUrl(u);
    if (href && !attachments.includes(href) && href !== noticeUrl) attachments.push(href);
  });
  const contact = [raw.contact_name, raw.contact_title, raw.contact_email].some(Boolean)
    ? { name: raw.contact_name, title: raw.contact_title, email: raw.contact_email }
    : null;

  const tiles = [];
  if (hasValue) {
    const native = valueSource === 'usd_actual' ? formatNativeValue(raw) : null;
    const usdShort = formatUsdShort(raw.deal_value_usd);
    const foreign = native && String(raw.currency).toUpperCase() !== 'USD';
    tiles.push(
      <FactTile
        key="value"
        label="Deal value"
        note={valueSource === 'neo' ? 'NEO estimate' : [foreign && usdShort ? `≈ ${usdShort}` : null, bundleNote].filter(Boolean).join(' · ') || null}
      >
        {native || <DealValueDisplay deal={raw} showOriginalCurrency />}
      </FactTile>,
    );
  }
  if (awarded) tiles.push(<FactTile key="awarded" label="Awarded" note={[method, framework ? `via ${framework}` : null].filter(Boolean).join(' · ') || null}>{awarded}</FactTile>);
  tiles.push(
    Number.isFinite(term) && term > 0 ? (
      <FactTile key="term" label="Term" note={Number.isFinite(extension) && extension > 0 ? `+ ${extension}-month extension` : null}>{term} months</FactTile>
    ) : (
      <FactTile key="term" label="Term"><span className="text-base font-normal text-[#5C6B7A]">Not published</span></FactTile>
    ),
  );
  if (hwSystem) {
    tiles.push(
      <FactTile key="age" label="Equipment age" note={HW_BAND_TEXT[hwBand]} accent={hwBand === 'plan' || hwBand === 'due' || hwBand === 'overdue'}>
        {hwAge == null ? <span className="text-base font-normal text-[#5C6B7A]">Not published</span> : `${Math.floor(hwAge)} years`}
      </FactTile>,
    );
  } else if (hwKind === 'consumable') {
    tiles.push(<FactTile key="kind" label="Purchase" note="Does not date the installed system">Consumables</FactTile>);
  }
  if (showExpiry && !hwSystem) {
    tiles.push(
      <FactTile
        key="expiry"
        label={expired ? 'Ended' : 'Predicted expiry'}
        note={<span className="inline-flex items-center gap-1.5"><GradeBadge grade={grade} />{!expired ? monthsAwayText(months) : null}</span>}
      >
        {lowGrade ? `~${new Date(expiry).getFullYear()}` : monthYear(expiry)}
      </FactTile>,
    );
  }
  // Every field that exists gets shown; bidders used to appear only when the window was closed.
  if (bidders != null && bidders !== '') tiles.push(<FactTile key="bidders" label="Bidders">{String(bidders)}</FactTile>);
  if (windowOpen && !hwSystem) tiles.push(<FactTile key="window" label="Decision window" accent>Open now</FactTile>);

  return (
    <div className="min-h-screen bg-[#EDF3FB]">
      <header className="bg-[#0A1A2F] text-[#F4F9FF]">
        <div className="max-w-6xl mx-auto px-6 pt-5 pb-9">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2 text-[13px] text-[#A9BBD1]">
              <button
                type="button"
                onClick={onBack}
                className="inline-flex items-center gap-1.5 min-h-[44px] pl-2.5 pr-3.5 mr-2 rounded-lg border border-white/30 text-white text-sm font-medium hover:bg-white/10"
              >
                <ChevronLeft className="w-4 h-4" aria-hidden />
                Back
              </button>
              <span>Closed Deals</span>
              {category ? <><span aria-hidden>/</span><span>{category}</span></> : null}
              {country ? <><span aria-hidden>/</span><span>{country}</span></> : null}
            </div>
            {statusLabel ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#00C2C2]/15 px-2.5 py-1 text-xs font-semibold text-[#5FE0E0]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#5FE0E0]" aria-hidden />
                {statusLabel}
              </span>
            ) : null}
          </div>
          <div className="mt-7 flex flex-wrap items-end justify-between gap-6">
            <div className="min-w-0">
              {institutionLine ? <div className="text-[13px] text-[#A9BBD1]">{institutionLine}</div> : null}
              <h1 className="mt-1.5 text-3xl md:text-4xl font-semibold leading-tight">{product || supplier || 'Closed deal'}</h1>
              <div className="mt-1.5 text-[15px] text-[#D6E4F5]">
                {[category, supplier ? `supplied by ${supplier}` : null].filter(Boolean).join(' · ')}
              </div>
            </div>
            {accountPageId ? (
              <Link
                to={createPageUrl(`AccountFinal?id=${encodeURIComponent(accountPageId)}`)}
                className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-lg bg-[#00C2C2] text-[#0A1A2F] text-sm font-semibold hover:bg-[#00A3A3]"
              >
                Open account
                <ArrowRight className="w-4 h-4" aria-hidden />
              </Link>
            ) : null}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 pb-16">
        <div className="mt-6 grid gap-px overflow-hidden rounded-2xl bg-[#D6E4F5] shadow-sm" style={{ gridTemplateColumns: `repeat(auto-fit, minmax(180px, 1fr))` }}>
          {tiles}
        </div>

        {hwSystem && hwAge != null ? (
          <Card className="mt-5">
            <h2 className="text-xl font-semibold text-[#0A1A2F]">Equipment life</h2>
            <p className="mt-1 text-sm text-[#5C6B7A]">
              Kit like this is usually replaced after {BAND_YEARS.plan}–{BAND_YEARS.due}+ years. The contract ending does not remove it.
            </p>
            {(() => {
              const span = BAND_YEARS.overdue + 3;
              const pct = (y) => `${Math.min(100, (y / span) * 100)}%`;
              return (
                <>
                  <div className="relative mt-11 mb-3 h-3.5 rounded-full bg-[#E2ECF9]" role="img" aria-label={`Installed ${awarded || ''}, ${Math.floor(hwAge)} years old`}>
                    <div className="absolute inset-y-0 rounded-none bg-[#F6E3B4]" style={{ left: pct(BAND_YEARS.plan), width: `calc(${pct(BAND_YEARS.due)} - ${pct(BAND_YEARS.plan)})` }} />
                    <div className="absolute inset-y-0 right-0 rounded-r-full" style={{ left: pct(BAND_YEARS.due), background: 'repeating-linear-gradient(135deg, #0A5A6B 0 6px, #2C7F8F 6px 12px)' }} />
                    <div className="absolute -top-3 -bottom-3 w-0.5 bg-[#0A1A2F]" style={{ left: pct(hwAge) }} />
                    <div className="absolute -top-8 -translate-x-1/2 whitespace-nowrap text-xs font-semibold text-[#0A1A2F]" style={{ left: pct(hwAge) }}>Today</div>
                  </div>
                  {/* Labels sit on the scale they describe, not in even thirds. */}
                  <div className="relative h-10 text-[13px] text-[#5C6B7A]">
                    <div className="absolute left-0"><strong className="font-semibold text-[#0A1A2F]">{fullDate(raw.first_award_date ?? raw.published_date)}</strong><br />Bought{supplier ? ` from ${supplier}` : ''}</div>
                    <div className="absolute -translate-x-1/2 text-center whitespace-nowrap" style={{ left: pct(BAND_YEARS.plan) }}><strong className="font-semibold text-[#0A1A2F]">{BAND_YEARS.plan} yrs</strong><br />Start planning</div>
                    <div className="absolute -translate-x-1/2 text-center whitespace-nowrap" style={{ left: pct(BAND_YEARS.due) }}><strong className="font-semibold text-[#0A1A2F]">{BAND_YEARS.due} yrs</strong><br />Due</div>
                    <div className="absolute -translate-x-1/2 text-center whitespace-nowrap" style={{ left: pct(BAND_YEARS.overdue) }}><strong className="font-semibold text-[#0A1A2F]">{BAND_YEARS.overdue} yrs</strong><br />Overdue</div>
                  </div>
                </>
              );
            })()}
          </Card>
        ) : null}

        {showExpiry && progress != null && !hwSystem ? (
          <Card className="mt-5">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-xl font-semibold text-[#0A1A2F]">Renewal timeline</h2>
            </div>
            <div className="relative mt-11 mb-3 h-3.5 rounded-full bg-[#E2ECF9]" role="img" aria-label={`Awarded ${awarded || ''}, predicted expiry ${monthYear(expiry) || ''}`}>
              <div className="absolute inset-y-0 left-0 rounded-l-full bg-[#1A9BB1]" style={{ width: `${progress}%` }} />
              {windowStart != null && !expired ? (
                <div
                  className={`absolute inset-y-0 right-0 rounded-r-full ${lowGrade ? 'opacity-50' : ''}`}
                  style={{ left: `${windowStart}%`, background: 'repeating-linear-gradient(135deg, #0A5A6B 0 6px, #2C7F8F 6px 12px)' }}
                />
              ) : null}
              <div className="absolute -top-3 -bottom-3 w-0.5 bg-[#0A1A2F]" style={{ left: `${progress}%` }} />
              <div className="absolute -top-8 -translate-x-1/2 whitespace-nowrap text-xs font-semibold text-[#0A1A2F]" style={{ left: `${progress}%` }}>Today</div>
            </div>
            <div className="grid grid-cols-3 gap-3 text-[13px] text-[#5C6B7A]">
              <div><strong className="font-semibold text-[#0A1A2F]">{fullDate(raw.first_award_date ?? raw.published_date)}</strong><br />Awarded{supplier ? ` to ${supplier}` : ''}</div>
              <div className="text-center">{!expired ? <><strong className="font-semibold text-[#0A1A2F]">Decision window</strong><br />Shaded: the last 12 months</> : null}</div>
              <div className="text-right"><strong className="font-semibold text-[#0A1A2F]">{lowGrade ? `~${new Date(expiry).getFullYear()}` : fullDate(expiry)}</strong><br />{expired ? 'Ended' : lowGrade ? 'Approximate expiry' : 'Predicted expiry'}</div>
            </div>
          </Card>
        ) : null}

        {sentimentOk ? (
          <Card className="mt-5">
            <Eyebrow>Only on NEO</Eyebrow>
            <h2 className="mt-1.5 text-xl font-semibold text-[#0A1A2F]">Incumbent position</h2>
            <div className="mt-5 flex gap-1.5" role="img" aria-label={`Incumbent sentiment: ${sentiment}`}>
              {['negative', 'neutral', 'positive'].map((lvl) => (
                <div key={lvl} className={`h-2.5 flex-1 rounded-full ${lvl === sentiment ? (lvl === 'negative' ? 'bg-[#C2410C]' : lvl === 'neutral' ? 'bg-[#8A9BB0]' : 'bg-[#1A9BB1]') : 'bg-[#E2ECF9]'}`} />
              ))}
            </div>
            <div className="mt-1.5 grid grid-cols-3 text-xs text-[#5C6B7A]">
              <span className={sentiment === 'negative' ? 'font-semibold text-[#0A1A2F]' : ''}>Negative</span>
              <span className={`text-center ${sentiment === 'neutral' ? 'font-semibold text-[#0A1A2F]' : ''}`}>Neutral</span>
              <span className={`text-right ${sentiment === 'positive' ? 'font-semibold text-[#0A1A2F]' : ''}`}>Positive</span>
            </div>
            <p className="mt-4 text-[15px] leading-relaxed text-[#0A1A2F]">
              {sentiment === 'positive'
                ? `${supplier || 'The incumbent'} looks secure here: expect a renewal rather than an open tender.`
                : sentiment === 'negative'
                  ? `${institution || 'The institution'} shows signs of dissatisfaction with ${supplier || 'the incumbent'}: an opening for a challenger.`
                  : `No strong signal either way on ${supplier || 'the incumbent'}.`}
            </p>
            {rationale ? <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-[#31435A]">{rationale}</p> : null}
          </Card>
        ) : null}

        {stack && stack.length ? (
          <Card className="mt-5">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-xl font-semibold text-[#0A1A2F]">What {institution || 'they'} run{institution ? 's' : ''}</h2>
            </div>
            <div className="mt-4 grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
              {stack.map((tile) => (
                <div key={tile.heading} className="rounded-xl border border-[#D6E4F5] p-3.5">
                  <div className="text-xs text-[#5C6B7A]">{tile.heading}</div>
                  <div className="mt-1 text-sm font-semibold text-[#0A1A2F]">{tile.vendors.slice(0, 2).join(' · ')}</div>
                  {tile.vendors.length > 2 ? <div className="mt-0.5 text-xs text-[#5C6B7A]">+{tile.vendors.length - 2} more</div> : null}
                </div>
              ))}
            </div>
          </Card>
        ) : null}

        {market || pricePeers ? (
          <div className="mt-5 grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
            {market ? (
              <Card>
                <h2 className="text-xl font-semibold text-[#0A1A2F]">{category} in {country}</h2>
                <div className="mt-1 text-[13px] text-[#5C6B7A]">
                  Top suppliers by number of {raw.institution_type ? `${String(raw.institution_type).toLowerCase()} buyers` : 'buyers'} ({market.total} in total)
                </div>
                <div className="mt-5 space-y-3.5 text-sm">
                  {market.rows.map((r) => (
                    <div key={r.key}>
                      <div className="flex justify-between gap-3"><span className="truncate">{r.label}</span><strong className="font-semibold">{r.institutions}</strong></div>
                      <div className="mt-1.5 h-2.5 rounded-full bg-[#E2ECF9]"><div className="h-2.5 rounded-full bg-[#1A9BB1]" style={{ width: `${r.pct}%` }} /></div>
                    </div>
                  ))}
                </div>
              </Card>
            ) : null}
            {pricePeers ? (
              <Card>
                <h2 className="text-xl font-semibold text-[#0A1A2F]">Price comparison</h2>
                <div className="mt-1 text-[13px] text-[#5C6B7A]">Same supplier or product in {country}</div>
                <div className="mt-3 text-sm">
                  {hasValue && formatUsdShort(raw.deal_value_usd) ? (
                    <div className="flex justify-between gap-3 border-b border-[#E2ECF9] py-2.5">
                      <span><strong className="font-semibold">{institution}</strong>{product ? ` · ${product}` : ''}{awarded ? ` · ${new Date(raw.first_award_date ?? raw.published_date).getFullYear()}` : ''}</span>
                      <span className="font-semibold">{formatUsdShort(raw.deal_value_usd)}</span>
                    </div>
                  ) : null}
                  {pricePeers.rows.map((p) => (
                    // Each comparison opens its own deal page; Back returns to this one (?deal= history).
                    <Link
                      key={p.deal_id}
                      to={createPageUrl(`Procurement?deal=${encodeURIComponent(p.deal_id)}`)}
                      className="flex justify-between gap-3 border-b border-[#E2ECF9] py-2.5 last:border-0 text-[#0A1A2F] hover:text-[#00A3A3]"
                    >
                      <span className="truncate underline-offset-2 hover:underline">{[p.account_name, p.product_name || p.supplier, safeDate(p.first_award_date)?.getFullYear()].filter(Boolean).join(' · ')}</span>
                      <span>{formatUsdShort(p.deal_value_usd)}</span>
                    </Link>
                  ))}
                </div>
              </Card>
            ) : null}
          </div>
        ) : null}

        {register || originalTitle || noticeUrl || contact || attachments.length ? (
          <Card className="mt-5">
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div className="min-w-0 flex-1">
                <h2 className="text-xl font-semibold text-[#0A1A2F]">Source</h2>
                {register ? (
                  <p className="mt-1.5 text-sm text-[#31435A]">
                    Published on {register}
                    {raw.source_award_id && !noticeUrl ? <> · reference <span className="font-mono text-[13px]">{String(raw.source_award_id).split('|')[0]}</span></> : null}
                  </p>
                ) : null}
                {originalTitle ? <p className="mt-1.5 text-sm text-[#31435A]">Original title: <em>{originalTitle}</em></p> : null}
                {contact ? (
                  <p className="mt-3 text-sm text-[#31435A]">
                    Deal contact: {[contact.name, contact.title].filter(Boolean).join(', ')}
                    {contact.email ? <>{contact.name || contact.title ? ' · ' : ''}<a href={`mailto:${contact.email}`} className="text-[#00A3A3] hover:underline">{contact.email}</a></> : null}
                  </p>
                ) : null}
                {attachments.length ? (
                  <div className="mt-3 flex flex-wrap gap-3">
                    {attachments.slice(0, 6).map((href, i) => (
                      <a key={href} href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-[#00A3A3] hover:underline">
                        <FileText className="w-3.5 h-3.5" aria-hidden />
                        Document {i + 1}
                      </a>
                    ))}
                  </div>
                ) : null}
              </div>
              {noticeUrl ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <a
                      href={noticeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-lg border border-[#D6E4F5] text-sm font-medium text-[#0A1A2F] hover:bg-[#F4F9FF]"
                    >
                      Open source notice
                      <ExternalLink className="w-4 h-4" aria-hidden />
                    </a>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs">Opens the published notice in a new tab</TooltipContent>
                </Tooltip>
              ) : null}
            </div>
          </Card>
        ) : null}
      </main>
    </div>
  );
}
